import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Divider,
  Grid,
  Image,
  FileInput,
  Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { IconDeviceFloppy, IconUpload } from '@tabler/icons-react';

interface CompanyInfo {
  name: string;
  logo: string;
  favicon: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    linkedin: string;
    twitter: string;
  };
}

export const CompanyInfoPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  
  const { token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  const form = useForm<CompanyInfo>({
    initialValues: {
      name: '',
      logo: '',
      favicon: '',
      address: '',
      email: '',
      phone: '',
      whatsapp: '',
      socialMedia: {
        facebook: '',
        instagram: '',
        linkedin: '',
        twitter: '',
      },
    },
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      form.setValues(response.data);
      // Set previews from existing URLs (they might be relative, convert to absolute if needed)
      if (response.data.logo) {
        const logoUrl = response.data.logo.startsWith('http') 
          ? response.data.logo 
          : `${API_URL}${response.data.logo}`;
        setLogoPreview(logoUrl);
      }
      if (response.data.favicon) {
        const faviconUrl = response.data.favicon.startsWith('http')
          ? response.data.favicon
          : `${API_URL}${response.data.favicon}`;
        setFaviconPreview(faviconUrl);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load company information',
        color: 'red',
      });
    } finally {
      setInitialLoading(false);
    }
  };

  // Handle file selection and preview
  const handleLogoFile = (file: File | null) => {
    setLogoFile(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    } else {
      setLogoPreview(null);
    }
  };

  const handleFaviconFile = (file: File | null) => {
    setFaviconFile(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFaviconPreview(previewUrl);
    } else {
      setFaviconPreview(null);
    }
  };

  const handleSubmit = async (values: CompanyInfo) => {
    setLoading(true);
    
    const formData = new FormData();
    // Append company info as JSON
    formData.append('data', JSON.stringify(values));
    // Append files if selected
    if (logoFile) {
      formData.append('logo', logoFile);
    }
    if (faviconFile) {
      formData.append('favicon', faviconFile);
    }
    
    try {
      await axios.put(`${API_URL}/settings/company`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      
      notifications.show({
        title: 'Success',
        message: 'Company information updated successfully',
        color: 'green',
      });
      
      // Refresh data and reset file inputs
      await fetchCompanyInfo();
      setLogoFile(null);
      setFaviconFile(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update company information',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Title order={2} mb="lg">Company Information</Title>

      <Paper withBorder p="xl" radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack>
                  <TextInput
                    label="Company Name"
                    placeholder="Enter company name"
                    {...form.getInputProps('name')}
                    required
                  />
                  
                  <TextInput
                    label="Email"
                    placeholder="info@company.com"
                    {...form.getInputProps('email')}
                    required
                  />
                  
                  <TextInput
                    label="Phone"
                    placeholder="+91 98765 43210"
                    {...form.getInputProps('phone')}
                    required
                  />
                  
                  <TextInput
                    label="WhatsApp"
                    placeholder="+91 98765 43210"
                    {...form.getInputProps('whatsapp')}
                  />
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack>
                  {/* ✅ Use Textarea for address */}
                  <Textarea
                    label="Address"
                    placeholder="Enter company address"
                    {...form.getInputProps('address')}
                    required
                    minRows={3}
                  />
                </Stack>
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Logo & Favicon" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack align="center">
                  {logoPreview && (
                    <Image
                      src={logoPreview}
                      alt="Company Logo Preview"
                      width={150}
                      height={150}
                      fit="contain"
                      mb="sm"
                    />
                  )}
                  <FileInput
                    label="Upload Logo"
                    placeholder="Click to upload"
                    accept="image/png,image/jpeg,image/svg+xml"
                    leftSection={<IconUpload size={16} />}
                    onChange={handleLogoFile}
                    w="100%"
                  />
                </Stack>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Stack align="center">
                  {faviconPreview && (
                    <Image
                      src={faviconPreview}
                      alt="Favicon Preview"
                      width={32}
                      height={32}
                      fit="contain"
                      mb="sm"
                      loading="lazy" 
                    />
                  )}
                  <FileInput
                    label="Upload Favicon"
                    placeholder="Click to upload"
                    accept="image/x-icon,image/png"
                    leftSection={<IconUpload size={16} />}
                    onChange={handleFaviconFile}
                    w="100%"
                  />
                </Stack>
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Social Media Links" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Facebook"
                  placeholder="https://facebook.com/..."
                  {...form.getInputProps('socialMedia.facebook')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Instagram"
                  placeholder="https://instagram.com/..."
                  {...form.getInputProps('socialMedia.instagram')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="LinkedIn"
                  placeholder="https://linkedin.com/company/..."
                  {...form.getInputProps('socialMedia.linkedin')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Twitter"
                  placeholder="https://twitter.com/..."
                  {...form.getInputProps('socialMedia.twitter')}
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="xl">
              <Button 
                type="submit" 
                color="primary" 
                loading={loading}
                leftSection={<IconDeviceFloppy size={16} />}
                size="md"
              >
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};